import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { UserAvatar } from "@/components/ui/enhanced-avatar";
import { Mail, Calendar, Shield } from "lucide-react";

export default function Profile() {
  const { user, profile } = useAuth();

  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) {
      return profile.first_name;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();
    }
    if (profile?.first_name) {
      return profile.first_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getRoleLabel = () => {
    switch (profile?.role) {
      case 'admin':
        return 'Administrator';
      case 'business_owner':
        return 'Business Owner';
      case 'staff':
        return 'Staff';
      default:
        return 'User';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold">Profile</h1>
            <p className="text-muted-foreground">Manage your profile information</p>
          </div>

          <Card className="p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <UserAvatar
                src={profile?.avatar_url || undefined}
                alt={getDisplayName()}
                value={user?.email || profile?.first_name || 'user'}
                firstName={profile?.first_name}
                lastName={profile?.last_name}
                fallbackText={getInitials()}
                className="h-24 w-24 text-2xl"
              />

              <div className="flex-1 space-y-4 text-center sm:text-left w-full">
                <div>
                  <h2 className="text-2xl font-semibold">{getDisplayName()}</h2>
                  <p className="text-muted-foreground">{user?.email}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{user?.email}</span>
                  </div>

                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                      {getRoleLabel()}
                    </span>
                  </div>

                  {user?.created_at && (
                    <div className="flex items-center gap-2 justify-center sm:justify-start sm:col-span-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Member since {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
